import React, { useEffect, useState } from 'react';
import { contentApi } from '../../services/contentApi';
import Button from '../../components/UI/Button';
import ActionStatus from '../../components/UI/ActionStatus';
import { useAutoClear } from '../../hooks/useAutoClear';
import { CourseEnrollment } from '../../types/content';

type InterestRow = {
  id: number;
  userProfileId: number;
  userEmail: string;
  status: string;
  dateCreated: string;
};

const CourseEnrollmentAdminPanel: React.FC<{ courseId: number; courseTitle?: string }> = ({ courseId, courseTitle }) => {
  const [enrollments, setEnrollments] = useState<CourseEnrollment[]>([]);
  const [interests, setInterests] = useState<InterestRow[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusRowId, setStatusRowId] = useState<number | null>(null);
  useAutoClear(message, setMessage, 5000);
  useAutoClear(error, setError, 10000);

  const load = () => {
    contentApi.listCourseEnrollments(courseId)
      .then((r) => setEnrollments(r.data.enrollments || []))
      .catch(() => setError('Could not load enrollments.'));
    contentApi.listCourseInterests(courseId)
      .then((r) => setInterests((r.data.interests || []) as InterestRow[]))
      .catch(() => undefined);
  };

  useEffect(() => { load(); }, [courseId]);

  return (
    <div className="mt-8 space-y-6 rounded-2xl border border-gray-200 bg-white p-5">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Enrollments &amp; interest queue</h2>
        <p className="text-sm text-gray-600">{courseTitle || `Course #${courseId}`}</p>
      </div>
      <div>
        <h3 className="font-medium text-gray-900">Enrolled users</h3>
        <ul className="mt-2 space-y-2">
          {enrollments.length === 0 && <li className="text-sm text-gray-500">No enrollments yet.</li>}
          {enrollments.map((row) => (
            <li key={row.id} className="text-sm text-gray-700 border rounded-lg px-3 py-2 flex justify-between gap-2">
              <span>{row.userEmail || `User ${row.userProfileId}`}</span>
              <span className="text-gray-500">{row.status}{row.interviewPackageCount ? ` · ${row.interviewPackageCount} reviews` : ''}</span>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <h3 className="font-medium text-gray-900">Interest queue (seats full)</h3>
        <ul className="mt-2 space-y-2">
          {interests.length === 0 && <li className="text-sm text-gray-500">No interested candidates.</li>}
          {interests.map((row) => (
            <li key={row.id} className="text-sm text-gray-700 border rounded-lg px-3 py-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <span>{row.userEmail} · {new Date(row.dateCreated).toLocaleString()}</span>
              <Button
                size="sm"
                onClick={async () => {
                  setMessage(null);
                  setError(null);
                  setStatusRowId(row.id);
                  try {
                    const response = await contentApi.enrollFromInterest(courseId, row.id);
                    if (!response.data.success) {
                      setError(response.data.message || 'Could not enroll.');
                      return;
                    }
                    setMessage(response.data.message || 'Enrolled.');
                    load();
                  } catch (err: unknown) {
                    setError(err instanceof Error ? err.message : 'Could not enroll.');
                  }
                }}
              >
                Enroll now
              </Button>
              <ActionStatus message={statusRowId === row.id ? message : null} error={statusRowId === row.id ? error : null} />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default CourseEnrollmentAdminPanel;
